import { useEffect, Component } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import Home from './pages/Home';
import LegalPage from './pages/LegalPage';
import NotFound from './pages/NotFound';
import Admin from './pages/Admin';
import AuthCallback from './pages/AuthCallback';
import Header from './components/Header';
import TopBar from './components/TopBar';
import Footer from './components/Footer';
import StickyWhatsApp from './components/StickyWhatsApp';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-8">
          <div className="text-center max-w-md">
            <h1 className="font-display text-4xl font-bold text-[#1042B0]">Something went off-track</h1>
            <p className="mt-3 text-[#475569]">Please reload the page. Your challan settlement is one tap away.</p>
            <a href="/" className="mt-6 inline-flex rounded-xl bg-[#FF7700] px-6 py-3 font-bold text-white">Go Home</a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
        else el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
      return;
    }
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  const isAuthCallback = location.hash?.includes('session_id=');
  const isAdminArea = location.pathname.startsWith('/admin');
  const bareChrome = isAuthCallback || isAdminArea;

  if (isAuthCallback) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      {!bareChrome && <TopBar />}
      {!bareChrome && <Header />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/privacy" element={<LegalPage docKey="privacy" />} />
        <Route path="/terms" element={<LegalPage docKey="terms" />} />
        <Route path="/refund" element={<LegalPage docKey="refund" />} />
        <Route path="/disclaimer" element={<LegalPage docKey="disclaimer" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!bareChrome && <Footer />}
      {!bareChrome && <StickyWhatsApp />}
    </>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.6,
    });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
